import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  listEicrReports, deleteEicrReport, duplicateEicrReport, updateEicrReport,
} from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { ListFilterBar } from '../../components/ListFilterBar';
import { Plus, ArrowRight, Trash2, FileText, Settings2, Copy, Star } from 'lucide-react';

const EicrReportsList = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | templates | draft | sent
  const location = useLocation();
  const base = location.pathname.startsWith('/employee') ? '/employee' : '/admin';

  const load = () => {
    setLoading(true);
    listEicrReports()
      .then(setReports)
      .catch(() => toast.error('Failed to load reports'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm(`Delete report ${id}? This cannot be undone.`)) return;
    try {
      await deleteEicrReport(id);
      toast.success('Report deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const duplicate = async (id) => {
    try {
      const clone = await duplicateEicrReport(id);
      toast.success(`Duplicated as ${clone.id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Duplicate failed');
    }
  };

  const toggleTemplate = async (r) => {
    const nextIsTemplate = !r.isTemplate;
    try {
      await updateEicrReport(r.id, { isTemplate: nextIsTemplate });
      toast.success(nextIsTemplate ? 'Saved as template' : 'Removed from templates');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const applyTemplate = async (id) => {
    try {
      const clone = await duplicateEicrReport(id);
      toast.success(`New report from template: ${clone.id}`);
      // Jump straight into the editor so the engineer can just tweak
      // the date/observations for the new visit.
      window.location.href = `${base}/eicr-reports/${clone.id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create from template');
    }
  };

  const counts = useMemo(() => ({
    all: reports.length,
    templates: reports.filter((r) => r.isTemplate).length,
    draft: reports.filter((r) => !r.isTemplate && r.status !== 'sent').length,
    sent: reports.filter((r) => !r.isTemplate && r.status === 'sent').length,
  }), [reports]);

  const filteredReports = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = reports;
    if (statusFilter === 'templates') list = list.filter((r) => r.isTemplate);
    else if (statusFilter === 'draft') list = list.filter((r) => !r.isTemplate && r.status !== 'sent');
    else if (statusFilter === 'sent') list = list.filter((r) => !r.isTemplate && r.status === 'sent');
    if (!q) return list;
    return list.filter((r) => {
      const hay = `${r.customerName || ''} ${r.location || ''} ${r.customerAddress || ''} ${r.id || ''} ${r.testEngineer || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [reports, query, statusFilter]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="EICR Section K · Observation Reports"
        description="Photo-book style observation reports for on-site electrical findings. Add photos, tag each with a classification code (C1 / C2 / C3 / FI), and email the PDF straight to the customer. Tip: pin recurring sites as templates (star icon) — they stay at the top of the list and clone with one tap."
        actions={
          <div className="flex flex-wrap gap-2 items-center">
            <Link to={`${base}/eicr-reports/presets`} data-testid="eicr-presets-link">
              <Button variant="outline" data-testid="eicr-presets-btn">
                <Settings2 className="h-4 w-4 mr-2" /> Manage reasons
              </Button>
            </Link>
            <Link to={`${base}/eicr-reports/new`} data-testid="eicr-new-link">
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="eicr-new-btn">
                <Plus className="h-4 w-4 mr-2" /> New report
              </Button>
            </Link>
          </div>
        }
      />

      {loading ? (
        <div className="p-8 text-slate-500">Loading…</div>
      ) : reports.length === 0 ? (
        <div className="p-12 text-center bg-white border border-slate-200 rounded-md">
          <FileText className="h-8 w-8 mx-auto text-slate-300 mb-2" />
          <div className="text-slate-500 mb-3">No observation reports yet.</div>
          <Link to={`${base}/eicr-reports/new`}>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="eicr-empty-cta">
              <Plus className="h-4 w-4 mr-2" /> Create your first report
            </Button>
          </Link>
        </div>
      ) : (
        <>
          <ListFilterBar
            chips={[
              { key: 'all', label: 'All', count: counts.all },
              { key: 'templates', label: 'Templates', count: counts.templates, accent: 'amber', icon: Star },
              { key: 'draft', label: 'Drafts', count: counts.draft, accent: 'blue' },
              { key: 'sent', label: 'Sent', count: counts.sent, accent: 'emerald' },
            ]}
            activeChip={statusFilter}
            onChipChange={setStatusFilter}
            query={query}
            onQueryChange={setQuery}
            placeholder="Search by customer, location, engineer or id…"
            resultCount={filteredReports.length}
            totalCount={reports.length}
            resultNoun="report"
            testIdPrefix="eicr"
          />
          {/* Mobile: card list so Open/Delete stay visible without horizontal scroll */}
          <div className="grid gap-3 md:hidden" data-testid="eicr-list-mobile">
            {filteredReports.map((r) => (
              <div
                key={r.id}
                className={`border rounded-md p-3 ${
                  r.isTemplate
                    ? 'bg-amber-50/60 border-amber-200'
                    : 'bg-white border-slate-200'
                }`}
                data-testid={`eicr-card-${r.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-mono text-[11px] text-slate-500 break-all">{r.id}</div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {r.isTemplate && (
                      <span className="text-[11px] font-medium text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded inline-flex items-center gap-1">
                        <Star className="h-3 w-3 fill-amber-500 stroke-amber-600" /> Template
                      </span>
                    )}
                    {r.status === 'sent'
                      ? <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">Sent</span>
                      : <span className="text-[11px] font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">Draft</span>}
                  </div>
                </div>
                <div className="font-medium text-slate-900 text-base">{r.customerName || '—'}</div>
                <div className="text-sm text-slate-600 mt-0.5">{r.location || '—'}</div>
                {r.testDate && (
                  <div className="text-xs text-slate-500 mt-0.5">Test: {r.testDate}</div>
                )}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                  {r.isTemplate ? (
                    <Button
                      size="sm"
                      onClick={() => applyTemplate(r.id)}
                      className="flex-1 h-10 bg-amber-600 hover:bg-amber-700"
                      data-testid={`eicr-use-template-${r.id}`}
                    >
                      Use as template <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  ) : (
                    <Link
                      to={`${base}/eicr-reports/${r.id}`}
                      className="flex-1"
                      data-testid={`eicr-open-${r.id}`}
                    >
                      <Button variant="outline" size="sm" className="w-full h-10">
                        Open <ArrowRight className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </Link>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleTemplate(r)}
                    className={`h-10 w-10 p-0 ${r.isTemplate
                      ? 'text-amber-700 border-amber-300 bg-amber-100 hover:bg-amber-200'
                      : 'text-slate-500 hover:text-amber-700 hover:bg-amber-50'}`}
                    data-testid={`eicr-toggle-template-${r.id}`}
                    aria-label={r.isTemplate ? 'Unpin template' : 'Save as template'}
                    title={r.isTemplate ? 'Unpin template' : 'Save as template'}
                  >
                    <Star className={`h-4 w-4 ${r.isTemplate ? 'fill-amber-500 stroke-amber-600' : ''}`} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => duplicate(r.id)}
                    className="h-10 w-10 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                    data-testid={`eicr-duplicate-${r.id}`}
                    aria-label="Duplicate"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => remove(r.id)}
                    className="h-10 w-10 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200"
                    data-testid={`eicr-delete-${r.id}`}
                    aria-label="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {r.isTemplate && (
                  <Link
                    to={`${base}/eicr-reports/${r.id}`}
                    className="mt-2 block text-center text-xs text-amber-800 underline"
                    data-testid={`eicr-edit-template-${r.id}`}
                  >
                    Edit this template
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Desktop: original table */}
          <div className="hidden md:block bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium text-slate-600">Report</th>
                  <th className="px-4 py-3 font-medium text-slate-600">Customer</th>
                  <th className="px-4 py-3 font-medium text-slate-600">Location</th>
                  <th className="px-4 py-3 font-medium text-slate-600">Test date</th>
                  <th className="px-4 py-3 font-medium text-slate-600">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredReports.map((r) => (
                  <tr
                    key={r.id}
                    className={`hover:bg-slate-50 ${r.isTemplate ? 'bg-amber-50/50' : ''}`}
                    data-testid={`eicr-row-${r.id}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.id}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{r.customerName || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{r.location || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{r.testDate || '—'}</td>
                    <td className="px-4 py-3">
                      {r.isTemplate ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 bg-amber-100 border border-amber-300 px-2 py-1 rounded">
                          <Star className="h-3 w-3 fill-amber-500 stroke-amber-600" /> Template
                        </span>
                      ) : r.status === 'sent'
                        ? <span className="inline-flex text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded">Sent</span>
                        : <span className="inline-flex text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2 py-1 rounded">Draft</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 justify-end">
                        {r.isTemplate ? (
                          <button
                            onClick={() => applyTemplate(r.id)}
                            className="text-sm text-amber-800 hover:text-amber-900 inline-flex items-center gap-1 font-medium"
                            data-testid={`eicr-use-template-desktop-${r.id}`}
                          >
                            Use as template <ArrowRight className="h-3 w-3" />
                          </button>
                        ) : (
                          <Link
                            to={`${base}/eicr-reports/${r.id}`}
                            className="text-sm text-blue-700 hover:text-blue-800 inline-flex items-center gap-1"
                            data-testid={`eicr-open-desktop-${r.id}`}
                          >
                            Open <ArrowRight className="h-3 w-3" />
                          </Link>
                        )}
                        <button
                          onClick={() => toggleTemplate(r)}
                          className={r.isTemplate ? 'text-amber-600 hover:text-amber-700' : 'text-slate-300 hover:text-amber-600'}
                          data-testid={`eicr-toggle-template-desktop-${r.id}`}
                          aria-label={r.isTemplate ? 'Unpin template' : 'Save as template'}
                          title={r.isTemplate ? 'Unpin template' : 'Save as template'}
                        >
                          <Star className={`h-4 w-4 ${r.isTemplate ? 'fill-amber-500 stroke-amber-600' : ''}`} />
                        </button>
                        <button
                          onClick={() => duplicate(r.id)}
                          className="text-slate-400 hover:text-slate-700"
                          data-testid={`eicr-duplicate-desktop-${r.id}`}
                          aria-label="Duplicate"
                          title="Duplicate report"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => remove(r.id)}
                          className="text-slate-400 hover:text-rose-600"
                          data-testid={`eicr-delete-desktop-${r.id}`}
                          aria-label="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default EicrReportsList;
